import { computed } from 'vue'
import { useAuthStore } from '../stores/auth'
import { UserRole } from '../config/permissions'

// Debug gating for verbose permission logs
const DEBUG_PERMS = import.meta.env.VITE_DEBUG_PERMISSIONS === 'true'
const debug = (...args: any[]) => {
  if (DEBUG_PERMS) {
    console.log(...args)
  }
}

type RoleName = string

interface ResourceActionKey {
  resource: string
  action: string
}

const resourceActionRoleMap: Record<string, RoleName[]> = {
  'project:view': ['manager', 'data_viewer', 'data_analyst', 'data_collector', 'form_designer'],
  'project:manage': ['manager'],
  'project:export': ['manager', 'data_downloader', 'data_analyst'],
  'project:collect': ['manager', 'data_collector'],
  'project:analyze': ['manager', 'data_analyst'],
  'organization:manage': ['manager'],
  'form:design': ['manager', 'form_designer'],
  'data:quality': ['manager', 'data_analyst'],
}

function buildKey({ resource, action }: ResourceActionKey): string {
  return `${resource}:${action}`
}

/**
 * Thin facade over auth-store per-project permission maps.
 * Pass projectId for project-scoped checks; omit for org-scoped aggregate + virtual ORGANIZATION_MEMBER.
 */
export function usePermissions() {
  const authStore = useAuthStore()

  const isSuperuser = computed(() => Boolean(authStore.user?.is_superuser))
  const isOwner = computed(() => Boolean(authStore.isOwner))

  const loadUserPermissions = async (projectId?: number): Promise<RoleName[]> => {
    debug(`[usePermissions] loadUserPermissions project:`, projectId)
    if (!authStore.user?.id) {
      return []
    }

    if (projectId) {
      const roles = await authStore.getUserRolesInProject(projectId)
      return roles.map((role) => role.name as RoleName)
    }

    // Org / unspecified scope: union of roles across projects the user can access
    return loadAggregatedProjectRoles()
  }

  const loadAggregatedProjectRoles = async (): Promise<RoleName[]> => {
    const projects = authStore.getUserProjects()
    const collected = new Set<RoleName>()
    for (const pid of projects) {
      const roles = await authStore.getUserRolesInProject(pid)
      roles.forEach((r) => collected.add(r.name as RoleName))
    }
    return Array.from(collected)
  }

  const withOrgMemberRole = (roles: RoleName[], projectId?: number): RoleName[] => {
    if (!projectId && authStore.hasOrganization) {
      return Array.from(new Set([...roles, UserRole.ORGANIZATION_MEMBER]))
    }
    return roles
  }

  const hasPermission = async (permission: RoleName, projectId?: number): Promise<boolean> => {
    if (isSuperuser.value || isOwner.value) return true
    const roles = withOrgMemberRole(await loadUserPermissions(projectId), projectId)
    return roles.includes(permission)
  }

  const hasAnyPermission = async (permissions: RoleName[], projectId?: number): Promise<boolean> => {
    if (isSuperuser.value || isOwner.value) return true
    const roles = withOrgMemberRole(await loadUserPermissions(projectId), projectId)
    return permissions.some((p) => roles.includes(p))
  }

  const hasAllPermissions = async (permissions: RoleName[], projectId?: number): Promise<boolean> => {
    if (isSuperuser.value || isOwner.value) return true
    const roles = withOrgMemberRole(await loadUserPermissions(projectId), projectId)
    return permissions.every((p) => roles.includes(p))
  }

  const can = async ({ resource, action }: ResourceActionKey): Promise<boolean> => {
    if (isSuperuser.value || isOwner.value) return true
    const key = buildKey({ resource, action })
    const requiredRoles = resourceActionRoleMap[key] || []
    return hasAnyPermission(requiredRoles)
  }

  const clearPermissionsCache = (): void => {
    authStore.clearAllProjectRolesCache()
  }

  const debugPermissions = async () => {
    debug('=== DEBUG PERMISSIONS ===')
    debug('User:', authStore.user)
    debug('Is superuser:', isSuperuser.value)
    debug('Is owner:', isOwner.value)
    clearPermissionsCache()
    await authStore.loadUserPermissions()
    const roles = await loadUserPermissions()
    debug('Aggregated roles:', roles)
    debug('========================')
  }

  if (typeof window !== 'undefined') {
    ;(window as any).debugPermissions = debugPermissions
  }

  return {
    loadUserPermissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    can,
    clearPermissionsCache,
  }
}
